import React from 'react'
import dayjs from 'dayjs'
import {
  Box,
  Stack,
  Divider,
  TextInput,
  Button,
  Group,
  Select,
} from '@mantine/core'
import LineChartComponent from '../../stories/LineChart/LineChart'
import { DataPoint, useMetrics } from '../api'
import { useWidgetContext } from '../providers'
import { WidgetWrapper } from '../WidgetWrapper'
import { z } from 'zod'
import { useWidgetEditor } from '../state'
import { COMPONENTS, STATISTIC } from '../schemas'
import { fmt } from '../format'
import { LineChartOptions } from '../schemas'
import { useMetricFormatting } from '../api'
import classes from './common.module.css'

type OptionsType = z.infer<typeof LineChartOptions>

export default function LineChart(props: { id: string }) {
  const ctx = useWidgetContext()
  const editor = useWidgetEditor()
  const config = editor.useConfig(props.id) || {
    widget: 'LineChart',
    options: {},
  }

  const { data: metrics } = useMetrics()
  const { data: formatting } = useMetricFormatting()

  if (config.widget !== 'LineChart')
    throw new Error('Invalid widget type, expected LineChart')

  const options: OptionsType = config.options

  // Generate months from competition start to end
  const months = React.useMemo(() => {
    if (!ctx.competition_start || !ctx.competition_end) {
      // Fallback to year-based generation if competition dates not available
      const contextPeriod = ctx.period || '2025-01-01'
      const year = dayjs(contextPeriod).year()
      return Array.from({ length: 12 }, (_, i) => {
        const date = dayjs(`${year}-01-01`).add(i, 'month')
        return {
          period: date.startOf('month').format('YYYY-MM-DD'),
          label: date.format('MMM'),
        }
      })
    }

    const startDate = dayjs(ctx.competition_start).startOf('month')
    const endDate = dayjs(ctx.competition_end).endOf('month')
    const monthsArray = []

    let currentDate = startDate
    while (
      currentDate.isBefore(endDate) ||
      currentDate.isSame(endDate, 'month')
    ) {
      monthsArray.push({
        period: currentDate.format('YYYY-MM-DD'),
        label: currentDate.format('MMM'),
      })
      currentDate = currentDate.add(1, 'month')
    }

    return monthsArray
  }, [ctx.competition_start, ctx.competition_end, ctx.period])

  // Get metric, component, and statistic from options first, then context
  const metricKey = options.metric || ctx.metric || ''
  const component = options.component || ctx.component || 'actual'
  const statistic = options.statistic || ctx.statistic || 'value'
  const metric = metrics.find((m) => m.key === metricKey)
  const title = options.title || metric?.name || 'Monthly Trend'

  // Create a formatter function using the new fmt function
  // This is called at the component level (following Rules of Hooks)
  const formatter = React.useCallback(
    (value: number | null | undefined) => {
      return fmt(metricKey, component, statistic, value, formatting)
    },
    [metricKey, component, statistic, formatting]
  )

  return (
    <WidgetWrapper
      id={props.id}
      transformData={(data: DataPoint[]) => {
        // Create a map of period to value
        const periodValues = new Map<string, number>()
        data.forEach((point) => {
          if (point.value !== null) {
            periodValues.set(point.period, point.value)
          }
        })

        // Create chart data for all months with raw values
        const chartData = months.map((month) => {
          const value = periodValues.get(month.period)
          return {
            label: month.label,
            value: value !== undefined ? value : null,
          }
        })

        return { chartData, title }
      }}
      render={({ chartData, title }) => (
        <Box className={classes.chart}>
          <LineChartComponent
            data={chartData}
            valueLabel={title}
            formatter={formatter}
          />
        </Box>
      )}
      query={() => ({
        period_type: 'month',
        period_range: false,
        periods: months.map((m) => m.period),
        subject_type: ctx.subject_type || 'user',
        subject_ids: [ctx.subject || 0],
        metrics: [metricKey],
        components: [component],
        statistics: [statistic],
        competition_id: ctx.competition_id,
      })}
    />
  )
}

// Form component for LineChart options
export function LineChartForm({ id }: { id: string }) {
  const editor = useWidgetEditor()
  const isSaving = editor.useIsSaving()
  const config = editor.useConfig(id)
  const [formValues, setFormValues] = React.useState(config.options)
  const { data: metrics } = useMetrics()

  if (config.widget !== 'LineChart')
    throw new Error('Invalid widget type, expected LineChart')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    editor.setOptions(formValues)
    editor.save()
  }

  // Prepare metrics for select with empty option
  const metricOptions = [
    { value: '', label: '' },
    ...metrics.map((metric) => ({
      value: metric.key,
      label: metric.name,
    })),
  ]

  // Prepare component and statistic options, replacing the empty option label
  const componentOptions = COMPONENTS.map((opt) =>
    opt.value === '' ? { value: '', label: '' } : opt
  )

  const statisticOptions = STATISTIC.map((opt) =>
    opt.value === '' ? { value: '', label: '' } : opt
  )

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="sm">
        <Divider label="Line Chart Options" />
        <TextInput
          label="Title"
          value={formValues.title || ''}
          onChange={(e) =>
            setFormValues({ ...formValues, title: e.currentTarget.value })
          }
          placeholder="Monthly Trend"
        />
        <Select
          label="Metric"
          value={formValues.metric || ''}
          onChange={(value) =>
            setFormValues({ ...formValues, metric: value || '' })
          }
          data={metricOptions}
          placeholder="Select a metric"
          searchable
          clearable
        />
        <Select
          label="Component"
          value={formValues.component || ''}
          onChange={(value) =>
            setFormValues({ ...formValues, component: value || '' })
          }
          data={componentOptions}
          placeholder="Select a component"
        />
        <Select
          label="Statistic"
          value={formValues.statistic || ''}
          onChange={(value) =>
            setFormValues({ ...formValues, statistic: value || '' })
          }
          data={statisticOptions}
          placeholder="Select a statistic"
        />
      </Stack>

      <Group justify="flex-end" mt="md">
        <Button color="red.4" variant="outline" onClick={editor.endEditWidget}>
          Cancel
        </Button>
        <Button type="submit" loading={isSaving}>
          Save
        </Button>
      </Group>
    </form>
  )
}

export const LineChartRegistry = {
  component: LineChart,
  formComponent: LineChartForm,
  mock: () => (
    <Box
      p="md"
      style={{ backgroundColor: 'white', borderRadius: '8px', height: '200px' }}
    >
      <LineChartComponent
        data={[
          { label: 'Jan', value: 85 },
          { label: 'Feb', value: 92 },
          { label: 'Mar', value: 88 },
          { label: 'Apr', value: 95 },
          { label: 'May', value: 91 },
        ]}
        valueLabel="Monthly Trend"
        formatter={(value) => value?.toFixed(0) + '%'}
      />
    </Box>
  ),
  getInitialOptions: () => LineChartOptions.parse({}),
}
